import {
    enrollBiometricsApi,
    getAuthenticationOptionsApi,
    biometricsLoginApi,
    getBiometricRegistrationOptionsApi,
} from './biometrics.api';

/**
 * @name enrollBiometrics - register biometrics details of user
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.token - access_token
 * @param {Object} Obj.credential - credential for registration
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} token - access_token
 * @param {Object} credential - credentials for registration
 * @returns {Promise<string>} status - status
 */
export const enrollBiometrics = async ({
    graphql,
    deviceId,
    credential,
    biometricsProvider,
    appleIdentifier,
}) => {
    try {
        const data = await enrollBiometricsApi({
            graphql,
            deviceId,
            credential,
            biometricsProvider,
            appleIdentifier,
        });
        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name getBiometricRegistrationOptions - to get the challenge for registration
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.deviceId - deviceId
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} deviceId - deviceId
 * @returns {challenge} - challenge to generate the public key for registration
 */
export const getBiometricRegistrationOptions = async ({ graphql, deviceId }) => {
    try {
        const data = await getBiometricRegistrationOptionsApi({ graphql, deviceId });
        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name getAuthenticationOptions - to get the challenge for registration
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.deviceId - deviceId
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} deviceId - deviceId
 * @returns {status} - status code
 */
export const getAuthenticationOptions = async ({ graphql, credentialId, deviceId }) => {
    try {
        const data = await getAuthenticationOptionsApi({ graphql, credentialId, deviceId });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name registerBiometricCredentials - Register biometrics
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @returns {publicKey} - publicKey
 */
export const registerBiometricCredentials = async ({ graphql, username, deviceId }) => {
    try {
        // Handle callback from iOS
        const handleAppleAuthEnrollResult = async function (e) {
            try {
                console.log('Event received:', e);
                const detail = e?.detail;
                if (detail?.status === 'success') {
                    const status = await enrollBiometrics({
                        graphql,
                        deviceId,
                        biometricsProvider: 'APPLE',
                        appleIdentifier: detail?.appleIdentifier,
                    });

                    // Call function to store credential
                    if (!window.indexedDB) {
                        console.error("Your browser doesn't support IndexedDB.");
                    } else {
                        updateDataToEirAppDB({
                            biometricsProvider: 'APPLE',
                        });
                    }

                    if (status) {
                        // sessionStorage.setItem('Enable_biometrics', true);
                        sessionStorage.setItem('Biometrics_Provider', 'APPLE');
                        sessionStorage.setItem('Biometrics_opted_in', true);
                        sessionStorage.setItem('Biometrics_enrollment_status', 'success');
                    }
                    return status;
                }
                window.removeEventListener('appleAuthEnrollResult', handleAppleAuthEnrollResult);
            } catch (error) {
                console.log('iOS biometrics enroll error:', error);
                sessionStorage.setItem('Biometrics_opted_in', false);
                sessionStorage.setItem('Biometrics_enrollment_status', 'failed');
            }
        };
        window.addEventListener('appleAuthEnrollResult', handleAppleAuthEnrollResult);
        // Native iOS app - APPLE registration
        if (window?.webkit?.messageHandlers?.appleAuthEnroll != null) {
            window.webkit.messageHandlers.appleAuthEnroll.postMessage({});
        } else {
            // WEBAUTHN registration
            if (!window.PublicKeyCredential) {
                console.log('WEBAUTHN is not supported by your browser.'); //TODO: update with error handling
                return;
            }
            const originalCredentialCreationOptions = await getBiometricRegistrationOptions({
                graphql,
                deviceId,
            });
            console.log('user id:', new Uint8Array(originalCredentialCreationOptions.user.id.data));
            const credentialCreationOptions = {
                ...originalCredentialCreationOptions,
                challenge: new Uint8Array(originalCredentialCreationOptions.challenge.data),
                rp: {
                    name: originalCredentialCreationOptions.rp.name,
                },
                user: {
                    // id: Uint8Array.from(username, c => c.charCodeAt(0)),
                    id: new Uint8Array(originalCredentialCreationOptions.user.id.data),
                    name: username,
                    displayName: username,
                },
            };

            const credential = await navigator.credentials.create({
                publicKey: credentialCreationOptions,
            });

            const credentialId = bufferToBase64(credential.rawId);

            const data = {
                rawId: credentialId,
                response: {
                    attestationObject: bufferToBase64(credential.response.attestationObject),
                    clientDataJSON: bufferToBase64(credential.response.clientDataJSON),
                    id: credential.id,
                    type: credential.type,
                },
            };
            const status = await enrollBiometrics({
                graphql,
                deviceId,
                credential: data,
                biometricsProvider: 'WEBAUTHN',
            });
            // Call function to store credential
            if (!window.indexedDB) {
                console.error("Your browser doesn't support IndexedDB.");
            } else {
                updateDataToEirAppDB({
                    biometricsProvider: 'WEBAUTHN',
                    credentialId: credentialId,
                });
            }
            if (status) {
                // sessionStorage.setItem('Enable_biometrics', true);
                sessionStorage.setItem('Biometrics_Provider', 'WEBAUTHN');
                sessionStorage.setItem('Biometrics_enrollment_status', 'success');
            }
            return status;
        }
    } catch (error) {
        console.error('Failed to register biometric credentials:', error.message);
        sessionStorage.setItem('Biometrics_enrollment_status', 'failed');
        alert('Failed to register biometrics. Please try again.');
    }
};

/**
 * @name authenticateBiometricCredentials - Authenticate biometrics
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @returns {publicKey} - publicKey
 */
export const authenticateBiometricCredentials = async ({
    graphql,
    offlineToken,
    deviceId,
    credentialId,
    biometricsProvider,
    appleIdentifier,
}) => {
    try {
        if (biometricsProvider === 'APPLE') {
            const response = await biometricsLogin({
                graphql,
                offlineToken,
                deviceId,
                biometricsProvider,
                appleIdentifier,
            });
            return response;
        } else if (biometricsProvider === 'WEBAUTHN') {
            if (!window.PublicKeyCredential) {
                console.log('WEBAUTHN is not supported by your browser.'); //TODO: update with error handling
                return;
            }
            const credentials = getDataFromEirAppDB('user_credential').then(credential => {
                if (credential) {
                    console.log('Credential:', credential);
                } else {
                    console.log('No credential stored.');
                }
            });

            const originalCredentialRequestOptions = await getAuthenticationOptions({
                graphql,

                credentialId: credentialId,
                deviceId,
            });

            const credentialRequestOptions = {
                ...originalCredentialRequestOptions,
                challenge: new Uint8Array(
                    originalCredentialRequestOptions.data.getBiometricsAuthenticationOptions.challenge.data
                ),
                allowCredentials: [
                    {
                        id: base64ToBuffer(credentialId),
                        type: 'public-key',
                        transports: ['internal'],
                    },
                ],
            };
            const credential = await navigator.credentials.get({
                publicKey: credentialRequestOptions,
            });
            const data = {
                rawId: bufferToBase64(credential.rawId),
                response: {
                    authenticatorData: bufferToBase64(credential.response.authenticatorData),
                    signature: bufferToBase64(credential.response.signature),
                    userHandle: bufferToBase64(credential.response.userHandle),
                    clientDataJSON: bufferToBase64(credential.response.clientDataJSON),
                    id: credential.id,
                    type: credential.type,
                },
            };
            const response = await biometricsLogin({
                graphql,
                offlineToken,
                deviceId,
                credentials: data,
                biometricsProvider,
            });
            return response;
        }
    } catch (error) {
        console.log('Failed to authenticate biometric credentials:', error.message);
        throw error;
    }
};

export const bufferToBase64 = buffer => btoa(String.fromCharCode(...new Uint8Array(buffer)));
export const base64ToBuffer = base64 => Uint8Array.from(atob(base64), c => c.charCodeAt(0));

export const arrayBufferToBase64URL = buffer => {
    let binary = '';
    let bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-') // Replace '+' with '-'
        .replace(/\//g, '_') // Replace '/' with '_'
        .replace(/=+$/, ''); // Remove '=' padding
};

/**
 * @name biometricsLogin - to authenticate the user
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.deviceId - deviceId
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} deviceId - deviceId
 * @returns {status} - status code
 */
export const biometricsLogin = async ({
    graphql,
    offlineToken,
    deviceId,
    credentials,
    biometricsProvider,
    appleIdentifier,
}) => {
    try {
        const data = await biometricsLoginApi({
            graphql,
            offlineToken,
            deviceId,
            credentials,
            biometricsProvider,
            appleIdentifier,
        });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};

// CRUD APIs for indexedDb
export const openEirAppDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('eirApp', 1);
        request.onupgradeneeded = event => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('mfa')) {
                db.createObjectStore('mfa', { keyPath: 'id' });
            }
        };
        request.onsuccess = () => {
            resolve(request.result);
        };
        request.onerror = () => {
            reject('Database failed to open!');
        };
    });
};

export const addDataToEirAppDB = async (key, value) => {
    const db = await openEirAppDB();
    const transaction = db.transaction('mfa', 'readwrite');
    const store = transaction.objectStore('mfa');

    store.put({ id: key, value: value });

    transaction.oncomplete = function () {
        console.log('Credential stored successfully in IndexedDB');
    };
};

export const updateDataToEirAppDB = async newData => {
    const db = await openEirAppDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction('mfa', 'readwrite');
        const store = transaction.objectStore('mfa');

        const key = 'user_credential';
        const getReq = store.get(key);

        getReq.onsuccess = () => {
            // const existing = getReq.result || { id: key };
            // const updated = { ...existing, ...newData };

            const existing = getReq.result || {};
            const updated = { id: key, ...existing, ...newData };
            const putReq = store.put(updated);
            putReq.onerror = reject;
            putReq.onsuccess = () => {
                console.log('IndexedDB updated:', updated);
            };
        };
        getReq.onerror = function () {
            console.log('Failed to update data');
            reject('get failed');
        };
        transaction.oncomplete = function () {
            console.log('Credential stored successfully in IndexedDB');
            resolve(true);
        };
        transaction.onerror = function (e) {
            console.log('Transaction Failed to update data', e);
            reject('failed', e);
        };
    });
};

export const getDataFromEirAppDB = async key => {
    const db = await openEirAppDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction('mfa', 'readonly');
        const store = transaction.objectStore('mfa');
        // Retrieve data from eirApp DB
        const getRequest = store.get(key);

        getRequest.onsuccess = function () {
            if (getRequest.result) {
                resolve(getRequest.result);
            } else {
                console.warn('No data found in IndexedDB.');
                resolve({});
            }
        };

        getRequest.onerror = function () {
            console.error('Error retrieving data:', getRequest.error);
            reject(getRequest.error);
        };
    });
};

export const deleteDataFromEirAppDB = async (key, fieldToDelete) => {
    try {
        const existingData = await getDataFromEirAppDB(key);
        const db = await openEirAppDB();
        const transaction = db.transaction('mfa', 'readwrite');
        const store = transaction.objectStore('mfa');

        if (
            typeof existingData === 'object' &&
            existingData !== null &&
            Object.prototype.hasOwnProperty.call(existingData, fieldToDelete)
        ) {
            delete existingData[fieldToDelete];
            if (!existingData.id || existingData.id !== key) {
                console.warn(`setting incorrect id. was ${existingData.id}, should be ${key}`);
                existingData.id = key;
            }
            store.delete(key);
            const updateRequest = store.add(existingData);

            updateRequest.onsuccess = () => {
                console.log(`Deleted record with key: ${fieldToDelete}`);
            };

            updateRequest.onerror = () => {
                console.error(`Failed to delete record with key: ${updateRequest}`);
            };
        } else {
            console.log(`Field ${fieldToDelete} not found for key in DB`);
        }
    } catch (e) {
        console.error('error in deleteDataFromEirAppDB');
    }
};
